import { Link, Navigate, Outlet, useLocation } from "react-router-dom";
import { ROLES } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import { Spinner } from "@/components/Field";
import { NAV } from "./nav";
import { Sidebar } from "./Sidebar";
import { Header } from "./Header";
import { BottomNav } from "./BottomNav";

const Loading = () => (
  <div className="min-h-screen flex items-center justify-center text-slate-500 gap-2"><Spinner /> Caricamento…</div>
);

const Chooser = ({ memberships, onPick, superAdmin, onLogout }) => (
  <div data-testid="association-chooser" className="min-h-screen bg-slate-50 font-body flex items-center justify-center px-4">
    <div className="w-full max-w-md space-y-5">
      <div className="text-center">
        <img src="/logo-insieme.png" alt="Logo Insieme" className="h-24 w-24 object-contain mx-auto" />
        <h1 className="font-heading text-2xl font-bold text-slate-900 mt-3">Quale associazione vuoi aprire?</h1>
        <p className="text-slate-500 mt-1">Potrai cambiarla in qualsiasi momento in alto a destra.</p>
      </div>
      <div className="space-y-2">
        {memberships.map((m) => (
          <button key={m.association_id} data-testid={`choose-assoc-${m.association_id}`} onClick={() => onPick(m.association_id)}
            className="w-full flex items-center justify-between rounded-xl border border-slate-200 bg-white px-5 py-4 text-left hover:border-primary transition-colors">
            <span className="font-semibold text-slate-900">{m.name}</span><span className="text-sm text-slate-500">{ROLES[m.role]}</span>
          </button>
        ))}
        {superAdmin && <Link to="/piattaforma" data-testid="choose-platform" className="block text-center text-sm font-semibold text-primary py-2">Vai alla Piattaforma</Link>}
        <button data-testid="choose-logout" onClick={onLogout} className="block w-full text-center text-sm text-slate-500 hover:text-slate-700 py-2">Esci</button>
      </div>
    </div>
  </div>
);

const Blocked = () => (
  <div data-testid="feature-disabled" className="rounded-xl border border-slate-200 bg-white p-8 text-center text-slate-600">
    Questa funzione non è attiva per la tua associazione o non hai i permessi per usarla.
  </div>
);

export default function AppLayout() {
  const { user, current, assoc, can, memberships, switchAssociation, logout } = useAuth();
  const { pathname } = useLocation();
  const nav = NAV.find((n) => n.to !== "/" && n.perm && (pathname === n.to || pathname.startsWith(`${n.to}/`)));
  if (user === null) return <Loading />;
  if (user === false) return <Navigate to="/login" replace />;
  if (!current) {
    if (memberships.length > 1) return <Chooser memberships={memberships} onPick={switchAssociation} superAdmin={user.platform_role === "super_admin"} onLogout={logout} />;
    if (user.platform_role === "super_admin") return <Navigate to="/piattaforma" replace />;
    return (
      <div data-testid="no-membership" className="min-h-screen flex items-center justify-center p-6 text-center text-slate-600">
        Il tuo account non è collegato a nessuna associazione attiva. Contatta l'amministratore.
      </div>
    );
  }
  if (!assoc) return <Loading />;
  return (
    <div className="flex min-h-screen bg-slate-50 font-body text-slate-700">
      <Sidebar />
      <div className="flex-1 min-w-0">
        <Header />
        {assoc.status !== "active" && (
          <div data-testid="suspended-banner" className="bg-amber-100 text-amber-900 text-sm px-4 lg:px-8 py-2">
            Associazione sospesa: puoi consultare i dati ma non modificarli.
          </div>
        )}
        <main key={current.association_id} className="px-4 lg:px-8 py-6 lg:py-8 pb-28 lg:pb-10 space-y-6 max-w-[1400px]">
          {nav && !can(...nav.perm) ? <Blocked /> : <Outlet />}
        </main>
      </div>
      <BottomNav />
    </div>
  );
}
